import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Toaster, toast } from 'react-hot-toast';
import { Tooltip } from 'react-tooltip';
import { format } from 'date-fns';
import { BASE_URL } from '../config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faSyncAlt,
  faAngleDoubleLeft,
  faAngleLeft,
  faAngleRight,
  faAngleDoubleRight,
} from '@fortawesome/free-solid-svg-icons';

const AdminFeeProcessedHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    admission_date: '',
    discharged_date: '',
    trans_no: '',
    loa: '',
    member_name: '',
    member_type: '',
    member_id: '',
    provider: '',
    coverage: '',
    corporate: '',
    date_credited: '',
    check_no: '',
    cv_no: '',
    payment_ref_no: '',
    status: '',
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'date_credited',
    direction: 'desc',
  });

  const statusMap = {
    success: { name: 'Success', color: 'bg-green-100 text-green-800' },
    failed: { name: 'Failed', color: 'bg-red-100 text-red-800' },
  };

  const columns = [
    { key: 'admission_date', label: 'Admission', sortable: true },
    { key: 'discharged_date', label: 'Discharged', sortable: true },
    { key: 'trans_no', label: 'Trans No.', sortable: true },
    { key: 'loa', label: 'LOA', sortable: true },
    { key: 'member_name', label: 'Member', sortable: true },
    { key: 'member_type', label: 'Type', sortable: true },
    { key: 'member_id', label: 'Member ID', sortable: true },
    { key: 'provider', label: 'Provider', sortable: true },
    { key: 'coverage', label: 'Coverage', sortable: true },
    { key: 'transaction_amt', label: 'Trans Amt.', sortable: true },
    { key: 'actual_claim_amt', label: 'Claim Amt.', sortable: true },
    { key: 'withholding_tax', label: 'W. Tax', sortable: true },
    { key: 'ppd', label: 'PPD', sortable: true },
    { key: 'ppd_withholding_tax', label: 'PPD W. Tax', sortable: true },
    { key: 'net_payable', label: 'Net Payable', sortable: true },
    { key: 'corporate', label: 'Corporate', sortable: true },
    { key: 'date_credited', label: 'Credited', sortable: true },
    { key: 'check_no', label: 'Check No.', sortable: true },
    { key: 'cv_no', label: 'CV#', sortable: true },
    { key: 'payment_ref_no', label: 'Pay Ref.', sortable: true },
    { key: 'status', label: 'Status', sortable: true },
    { key: 'error_message', label: 'Error Message', sortable: false },
    { key: 'processed_at', label: 'Processed At', sortable: true },
  ];

  useEffect(() => {
    fetchProcessedHistory();
  }, [filters, sortConfig, currentPage, itemsPerPage, searchTerm]);

  const fetchProcessedHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/admin-fee/processed`, {
        params: {
          page: currentPage,
          per_page: itemsPerPage,
          search: searchTerm,
          sort_by: sortConfig.key,
          sort_direction: sortConfig.direction,
          ...filters,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
        },
      });
      setTransactions(response.data.data.data);
      setTotalPages(response.data.data.last_page);
    } catch (error) {
      if (error.response?.status === 401) window.location.href = '/login';
      if (error.response?.status === 403) {
        toast.error('You do not have permission to access this.', { duration: 4000 });
      }
      if (error.response?.status === 429) {
        toast.error('Rate limit exceeded. Please try again later.', { duration: 4000 });
      }
      toast.error('Failed to fetch processed history.', { duration: 4000 });
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc',
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const renderPageNumbers = () => {
    const pages = [];
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);
    if (currentPage <= 3) endPage = Math.min(5, totalPages);
    else if (currentPage >= totalPages - 2) startPage = Math.max(totalPages - 4, 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-2.5 py-0.5 rounded-md text-sm font-medium transition-colors ${
            currentPage === i ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-indigo-100'
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  const LoadingSkeleton = ({ rows = 5, columns = 23 }) => (
    <>
      {Array(rows)
        .fill()
        .map((_, i) => (
          <tr key={i} className="animate-pulse border-b border-gray-100">
            {Array(columns)
              .fill()
              .map((_, j) => (
                <td key={j} className="px-2 py-2.5 whitespace-nowrap">
                  <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                </td>
              ))}
          </tr>
        ))}
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 font-sans">
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
      <div className="max-w-1xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <div className="p-2 bg-white rounded-lg shadow-sm">
            <FontAwesomeIcon icon={faSyncAlt} className="text-indigo-600 text-base" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Processed Admin Fees</h1>
            <p className="text-xs text-gray-500 mt-1">Track and manage processed admin fee transactions</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-sm">
          {/* Top Controls */}
          <div className="p-4 sm:p-5 border-b border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="relative flex-1 max-w-md">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"
                />
                <input
                  type="text"
                  placeholder="Search transactions..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 transition-colors placeholder-gray-400"
                  aria-label="Search transactions"
                />
              </div>
              <button
                onClick={fetchProcessedHistory}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors shadow-sm"
                data-tooltip-id="refresh-tooltip"
                data-tooltip-content="Refresh transaction list"
              >
                <FontAwesomeIcon icon={faSyncAlt} className="text-xs" />
                Refresh
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="p-3 sm:p-4 bg-gray-50 border-b border-gray-100 overflow-x-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 min-w-[800px]">
              {columns.map(
                (col) =>
                  ['admission_date', 'discharged_date', 'trans_no', 'loa', 'member_name', 'member_type', 'member_id', 'provider', 'coverage', 'corporate', 'date_credited', 'check_no', 'cv_no', 'payment_ref_no', 'status'].includes(col.key) && (
                    <div key={col.key} className="min-w-[120px]">
                      <label className="text-xs text-gray-600 mb-1 block">{col.label}</label>
                      {col.key === 'status' ? (
                        <select
                          value={filters.status}
                          onChange={(e) => handleFilterChange('status', e.target.value)}
                          className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 transition-colors bg-white"
                          aria-label="Filter by status"
                        >
                          <option value="">All Statuses</option>
                          <option value="success">Success</option>
                          <option value="failed">Failed</option>
                        </select>
                      ) : ['admission_date', 'discharged_date', 'date_credited'].includes(col.key) ? (
                        <input
                          type="date"
                          value={filters[col.key]}
                          onChange={(e) => handleFilterChange(col.key, e.target.value)}
                          className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 transition-colors bg-white"
                          aria-label={`Filter by ${col.label}`}
                        />
                      ) : (
                        <input
                          type="text"
                          placeholder={col.label}
                          value={filters[col.key]}
                          onChange={(e) => handleFilterChange(col.key, e.target.value)}
                          className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 transition-colors placeholder-gray-400 bg-white"
                          aria-label={`Filter by ${col.label}`}
                        />
                      )}
                    </div>
                  )
              )}
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full table-auto text-xs">
              <thead className="bg-gray-50 text-gray-600 font-medium">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className={`px-2 py-2.5 text-left text-xs uppercase tracking-wide whitespace-nowrap ${
                        column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''
                      }`}
                      onClick={() => column.sortable && handleSort(column.key)}
                      aria-sort={
                        sortConfig.key === column.key
                          ? sortConfig.direction === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                    >
                      <div className="flex items-center gap-1">
                        {column.label}
                        {column.sortable && (
                          <div className="flex flex-col text-[8px]">
                            <span
                              className={`${
                                sortConfig.key === column.key && sortConfig.direction === 'asc'
                                  ? 'text-indigo-600'
                                  : 'text-gray-400'
                              }`}
                            >
                              ▲
                            </span>
                            <span
                              className={`${
                                sortConfig.key === column.key && sortConfig.direction === 'desc'
                                  ? 'text-indigo-600'
                                  : 'text-gray-400'
                              }`}
                            >
                              ▼
                            </span>
                          </div>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <LoadingSkeleton rows={itemsPerPage} columns={columns.length} />
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="px-2 py-4 text-center text-gray-500 text-xs">
                      No transactions found.
                    </td>
                  </tr>
                ) : (
                  transactions.map((transaction) => (
                    <tr key={transaction.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-2 py-2.5 text-gray-700 whitespace-nowrap">{transaction.admission_date ? format(new Date(transaction.admission_date), 'yyyy-MM-dd') : '-'}</td>
                      <td className="px-2 py-2.5 text-gray-700 whitespace-nowrap">{transaction.discharged_date ? format(new Date(transaction.discharged_date), 'yyyy-MM-dd') : '-'}</td>
                      <td className="px-2 py-2.5 text-gray-700 whitespace-nowrap">{transaction.trans_no || '-'}</td>
                      <td className="px-2 py-2.5 text-gray-700 whitespace-nowrap">{transaction.loa || '-'}</td>
                      <td className="px-2 py-2.5 text-gray-700 whitespace-nowrap">{transaction.member_name || '-'}</td>
                      <td className="px-2 py-2.5 text-gray-700 whitespace-nowrap">{transaction.member_type || '-'}</td>
                      <td className="px-2 py-2.5 text-gray-700 whitespace-nowrap">{transaction.member_id || '-'}</td>
                      <td className="px-2 py-2.5 text-gray-700 whitespace-nowrap">{transaction.provider || '-'}</td>
                      <td className="px-2 py-2.5 text-gray-700 whitespace-nowrap">{transaction.coverage || '-'}</td>
                      <td className="px-2 py-2.5 text-gray-700 whitespace-nowrap">{transaction.transaction_amt ? Number(transaction.transaction_amt).toFixed(2) : '-'}</td>
                      <td className="px-2 py-2.5 text-gray-700 whitespace-nowrap">{transaction.actual_claim_amt ? Number(transaction.actual_claim_amt).toFixed(2) : '-'}</td>
                      <td className="px-2 py-2.5 text-gray-700 whitespace-nowrap">{transaction.withholding_tax ? Number(transaction.withholding_tax).toFixed(2) : '-'}</td>
                      <td className="px-2 py-2.5 text-gray-700 whitespace-nowrap">{transaction.ppd ? Number(transaction.ppd).toFixed(2) : '-'}</td>
                      <td className="px-2 py-2.5 text-gray-700 whitespace-nowrap">{transaction.ppd_withholding_tax ? Number(transaction.ppd_withholding_tax).toFixed(2) : '-'}</td>
                      <td className="px-2 py-2.5 text-gray-700 whitespace-nowrap">{transaction.net_payable ? Number(transaction.net_payable).toFixed(2) : '-'}</td>
                      <td className="px-2 py-2.5 text-gray-700 whitespace-nowrap">{transaction.corporate || '-'}</td>
                      <td className="px-2 py-2.5 text-gray-700 whitespace-nowrap">{transaction.date_credited ? format(new Date(transaction.date_credited), 'yyyy-MM-dd') : '-'}</td>
                      <td className="px-2 py-2.5 text-gray-700 whitespace-nowrap">{transaction.check_no || '-'}</td>
                      <td className="px-2 py-2.5 text-gray-700 whitespace-nowrap">{transaction.cv_no || '-'}</td>
                      <td className="px-2 py-2.5 text-gray-700 whitespace-nowrap">{transaction.payment_ref_no || '-'}</td>
                      <td className="px-2 py-2.5 whitespace-nowrap">
                        <span
                          className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            statusMap[transaction.status]?.color || 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {statusMap[transaction.status]?.name || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-2 py-2.5 text-gray-700 whitespace-nowrap">{transaction.error_message || '-'}</td>
                      <td className="px-2 py-2.5 text-gray-700 whitespace-nowrap">{transaction.processed_at ? format(new Date(transaction.processed_at), 'yyyy-MM-dd HH:mm:ss') : '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 sm:p-5 border-t border-gray-100">
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="mb-3 sm:mb-0 px-2.5 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 transition-colors bg-white"
              aria-label="Items per page"
            >
              {[5, 10, 20, 50, 100].map((val) => (
                <option key={val} value={val}>
                  {val} per page
                </option>
              ))}
            </select>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1 || loading}
                className="p-1.5 rounded-md text-gray-600 hover:bg-indigo-100 disabled:opacity-50 transition-colors"
                aria-label="First page"
              >
                <FontAwesomeIcon icon={faAngleDoubleLeft} className="text-sm" />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="p-1.5 rounded-md text-gray-600 hover:bg-indigo-100 disabled:opacity-50 transition-colors"
                aria-label="Previous page"
              >
                <FontAwesomeIcon icon={faAngleLeft} className="text-sm" />
              </button>
              {renderPageNumbers()}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                className="p-1.5 rounded-md text-gray-600 hover:bg-indigo-100 disabled:opacity-50 transition-colors"
                aria-label="Next page"
              >
                <FontAwesomeIcon icon={faAngleRight} className="text-sm" />
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages || loading}
                className="p-1.5 rounded-md text-gray-600 hover:bg-indigo-100 disabled:opacity-50 transition-colors"
                aria-label="Last page"
              >
                <FontAwesomeIcon icon={faAngleDoubleRight} className="text-sm" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <Tooltip id="refresh-tooltip" place="top" />
    </div>
  );
};

export default AdminFeeProcessedHistory;